import React, { useState, useEffect, useCallback } from 'react'
import { fetchOptions } from '../../services/csrf'

interface UserAnnouncement {
  id: string
  title: string
  body: string | null
  starts_at: string
  ends_at: string | null
  priority: 'info' | 'warning' | 'critical'
  is_system_wide: boolean
  created_at: string
}

const PRIORITY_STYLES = {
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-900',
    body: 'text-blue-800',
    dismiss: 'text-blue-600 hover:text-blue-800 hover:bg-blue-100',
    modalBg: 'bg-blue-50',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-500',
    title: 'text-amber-900',
    body: 'text-amber-800',
    dismiss: 'text-amber-600 hover:text-amber-800 hover:bg-amber-100',
    modalBg: 'bg-amber-50',
  },
  critical: {
    container: 'bg-red-50 border-red-200',
    icon: 'text-red-500',
    title: 'text-red-900',
    body: 'text-red-800',
    dismiss: 'text-red-600 hover:text-red-800 hover:bg-red-100',
    modalBg: 'bg-red-50',
  },
} as const

const PRIORITY_ICONS = {
  info: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  ),
  warning: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  ),
  critical: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  ),
}

export function AnnouncementBanner(): React.ReactElement | null {
  const [announcements, setAnnouncements] = useState<UserAnnouncement[]>([])
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [criticalModal, setCriticalModal] = useState<UserAnnouncement | null>(null)
  const [acknowledging, setAcknowledging] = useState(false)

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/announcements', fetchOptions())
      if (!response.ok) return
      const data = await response.json()
      setAnnouncements(data.announcements || [])

      // Auto-show first critical announcement as modal
      const critical = (data.announcements || []).find(
        (a: UserAnnouncement) => a.priority === 'critical'
      )
      if (critical) {
        setCriticalModal(critical)
      }
    } catch {
      // Silently fail - announcements are non-critical
    }
  }, [])

  useEffect(() => {
    fetchAnnouncements()
  }, [fetchAnnouncements])

  const handleDismiss = async (announcement: UserAnnouncement) => {
    setAcknowledging(true)
    try {
      await fetch(`/api/v1/announcements/${announcement.id}/acknowledge`, fetchOptions('POST'))
      setDismissedIds(prev => new Set([...prev, announcement.id]))
      if (criticalModal?.id === announcement.id) {
        setCriticalModal(null)
      }
    } catch {
      // Still dismiss locally even if API fails
      setDismissedIds(prev => new Set([...prev, announcement.id]))
    }
    setAcknowledging(false)
  }

  const visibleAnnouncements = announcements.filter(a => !dismissedIds.has(a.id))
  const bannerAnnouncements = visibleAnnouncements.filter(a => a.priority !== 'critical' || a.id !== criticalModal?.id)

  if (visibleAnnouncements.length === 0 && !criticalModal) return null

  return (
    <>
      {/* Banner announcements (info + warning) */}
      {bannerAnnouncements.length > 0 && (
        <div className="space-y-0">
          {bannerAnnouncements.map(ann => {
            const styles = PRIORITY_STYLES[ann.priority]
            return (
              <div
                key={ann.id}
                className={`border-b ${styles.container}`}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                  <div className="flex items-start gap-3">
                    <svg className={`w-5 h-5 shrink-0 mt-0.5 ${styles.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      {PRIORITY_ICONS[ann.priority]}
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${styles.title}`}>{ann.title}</p>
                      {ann.body && (
                        <div
                          className={`text-sm mt-0.5 ${styles.body} [&_p]:mb-1 [&_ul]:list-disc [&_ul]:ml-4 [&_a]:underline`}
                          dangerouslySetInnerHTML={{ __html: ann.body }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDismiss(ann)}
                      className={`shrink-0 p-1 rounded transition-colors ${styles.dismiss}`}
                      title="Dismiss"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Critical announcement modal */}
      {criticalModal && !dismissedIds.has(criticalModal.id) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Red header bar */}
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="w-6 h-6 text-white shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                {PRIORITY_ICONS.critical}
              </svg>
              <h2 className="text-lg font-bold text-white">Critical Announcement</h2>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{criticalModal.title}</h3>

              {criticalModal.body && (
                <div
                  className="text-sm text-gray-700 mb-6 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_a]:text-blue-600 [&_a]:underline [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: criticalModal.body }}
                />
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleDismiss(criticalModal)}
                  disabled={acknowledging}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold text-sm rounded-lg transition-colors"
                >
                  {acknowledging ? 'Acknowledging...' : 'I Understand'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
